/**
 * Filter state lives in the URL, so every view is a shareable link (this
 * replaces the Looker "Link Shortner" button). The tenant is also remembered
 * per viewer in localStorage so a fresh visit opens on the last tenant.
 *
 * Filters stay in the URL when you move between pages, but each page sends
 * only the filters it offers (shared/tabFilters.js); the rest are listed as
 * "not used on this page" so nothing changes a page's numbers invisibly.
 *
 * `complete=1` makes the date presets end yesterday, so every day shown is a
 * whole day.
 */

import { useCallback, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { routeLocation } from "../../shared/catalog.js";
import { DATE_PRESETS, DEFAULT_PRESET, addDays, clampWindow, istToday } from "../../shared/dates.js";
import { filterKeysFor } from "../../shared/tabFilters.js";
import { DEFAULT_TENANT } from "../../shared/tenants.js";

const TENANT_KEY = "content-analytics:tenant";

/** Filters the API understands; everything else in the URL is UI state. */
export const API_FILTERS = ["platform", "specialty", "profession", "city", "state", "country", "source", "medium", "campaign", "contentSpecialty", "contentType"];

export function rememberedTenant() {
  try {
    return localStorage.getItem(TENANT_KEY) || DEFAULT_TENANT;
  } catch {
    return DEFAULT_TENANT;
  }
}

function remember(tenant) {
  try {
    localStorage.setItem(TENANT_KEY, tenant);
  } catch {
    // storage blocked; the URL still carries it
  }
}

export function useFilters() {
  const [params, setParams] = useSearchParams();
  const today = istToday();
  // Outside a report route (providers, login) this falls back to the default groups.
  const { dashboard, tab } = routeLocation(useParams());
  const pageKeys = useMemo(() => filterKeysFor(dashboard, tab), [dashboard, tab]);

  const filters = useMemo(() => {
    const preset = params.get("range") || (params.get("from") ? "custom" : DEFAULT_PRESET);
    const complete = params.get("complete") === "1";
    let from = params.get("from");
    let to = params.get("to");
    if (preset !== "custom" || !from || !to) {
      const p = DATE_PRESETS.find((x) => x.id === preset) ?? DATE_PRESETS.find((x) => x.id === DEFAULT_PRESET);
      ({ from, to } = p.range(complete ? addDays(today, -1) : today));
    }
    ({ from, to } = clampWindow(from, to, today));
    const out = {
      tenant: params.get("tenant") || rememberedTenant(),
      preset,
      complete,
      from,
      to,
      includeTest: params.get("includeTest") === "1",
    };
    for (const k of API_FILTERS) out[k] = params.get(k) || "";
    return out;
  }, [params, today]);

  const update = useCallback(
    (patch) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(patch)) {
            if (v === "" || v === null || v === undefined || v === false) next.delete(k);
            else next.set(k, v === true ? "1" : String(v));
          }
          if (patch.tenant) remember(patch.tenant);
          return next;
        },
        { replace: false },
      );
    },
    [setParams],
  );

  const setPreset = useCallback((id) => update({ range: id, from: "", to: "" }), [update]);
  const setRange = useCallback((from, to) => update({ range: "custom", from, to }), [update]);
  const setComplete = useCallback((on) => update({ complete: on }), [update]);

  /** Switching tenant drops profile and content filters: their values belong to the old tenant. */
  const setTenant = useCallback(
    (tenant) => update({ tenant, specialty: "", profession: "", city: "", state: "", country: "", contentSpecialty: "", contentType: "" }),
    [update],
  );

  const clear = useCallback(() => update(Object.fromEntries(API_FILTERS.map((k) => [k, ""]))), [update]);

  /** Query string for the API: only the filters this page offers. */
  const apiQuery = useMemo(() => {
    const q = new URLSearchParams({ tenant: filters.tenant, from: filters.from, to: filters.to });
    for (const k of API_FILTERS) if (filters[k] && pageKeys.includes(k)) q.set(k, filters[k]);
    if (filters.includeTest) q.set("includeTest", "1");
    return q.toString();
  }, [filters, pageKeys]);

  const activeCount = API_FILTERS.filter((k) => filters[k]).length;
  /** Filters set (on another page) that this page does not use. */
  const unusedHere = API_FILTERS.filter((k) => filters[k] && !pageKeys.includes(k));

  return { filters, update, setPreset, setRange, setComplete, setTenant, clear, apiQuery, activeCount, unusedHere, pageKeys, today };
}
